import { API_CONFIG } from "@/infrastructure/config/api";
import { getApi, postApi, putApi, delApi } from "@/shared/utils/fetchApi";
import type {
  InstalledSkill,
  InstalledSkillDetail,
  SkillCatalogDetail,
  SkillHistoryEntry,
  SkillInstallationScope,
  SkillResourceContent,
  SkillStorefrontEntry,
  SkillUpdatePreview,
  SkillVersion,
} from "../types/skill";

/**
 * Catálogo e instalação de Skills (AB#85631, AB#85633, AB#85634, AB#85637).
 *
 * **O escopo é um parâmetro, não um espaço de nomes de URL.** Havia uma raiz
 * por nível — `/me/skills`, `/organization/skills`, `/products/:id/skills` — e
 * cada nível novo exigiria um grupo de rotas novo para repetir as mesmas doze
 * operações. Agora há um caminho só e o nível viaja como `scope`.
 *
 * Isso não afrouxa nada: o que impede alguém de instalar no espaço de outra
 * pessoa nunca foi o formato da URL, e sim a autorização no servidor, que
 * continua sendo feita sobre o nível resolvido. Os IDENTIFICADORES seguem sem
 * vir do cliente — a organização sai da sessão e a pessoa do autenticado. Só o
 * workspace é informado, e ele já era visível no caminho antes.
 *
 * A base é a **raiz** da api (`API_ROOT_URL`), e não a de `/api`: as rotas de
 * skills são registradas na raiz. Um caminho relativo aqui herdaria o prefixo
 * `/api` do `fetchApi` e daria 404 — e como o 404 vira só "não foi possível
 * carregar" na tela, o sintoma não diz onde está o erro.
 */
const RAIZ_DAS_INSTALACOES = "/skills/installations";

/** Onde a tela está operando. `productId` só importa no nível de workspace. */
export interface SkillScopeTarget {
  scope: SkillInstallationScope;
  productId?: string | null;
}

export class SkillsService {
  /**
   * Monta o caminho com o escopo já na query.
   *
   * Existe como função única para que nenhuma chamada monte a query à mão e
   * esqueça o `scope` — o pedido sem nível é recusado pela api, e o sintoma
   * seria um erro genérico numa tela só.
   */
  private static rota(
    target: SkillScopeTarget,
    sufixo = "",
    extras: Record<string, string> = {},
  ): string {
    const query = new URLSearchParams({ scope: target.scope, ...extras });

    if (target.scope === "product") {
      if (!target.productId) throw new Error("productId is required for workspace-scoped skills");
      query.set("product_id", target.productId);
    }

    return `${API_CONFIG.API_ROOT_URL}${RAIZ_DAS_INSTALACOES}${sufixo}?${query.toString()}`;
  }

  /**
   * O catálogo inteiro com o estado de instalação de cada skill.
   *
   * É o oposto do que o agente recebe — ele vê só o instalado. Quem administra
   * precisa ver o que ainda não instalou, senão não teria como instalar nada.
   *
   * `installed` responde por **este nível, e só por ele** (AB#85634): cada visão
   * mostra as instalações que ela mesma fez. A skill instalada noutro nível vem
   * em `installedElsewhere`, dizendo QUAL — o cartão desabilita o Instalar com o
   * motivo à vista, em vez de deixar clicar e tomar recusa.
   */
  static async storefront(target: SkillScopeTarget): Promise<SkillStorefrontEntry[]> {
    const data = await getApi<{ skills: SkillStorefrontEntry[] }>(
      this.rota(target, "/storefront"),
    );

    return data.skills ?? [];
  }

  /**
   * O conteúdo da skill lido do catálogo, **sem precisar instalar**.
   *
   * É o que permite ler o procedimento antes de decidir: instalar uma skill é
   * dar um roteiro ao agente de quem trabalha naquele escopo.
   */
  static async catalogDetail(target: SkillScopeTarget, name: string): Promise<SkillCatalogDetail> {
    return getApi<SkillCatalogDetail>(this.rota(target, `/storefront/${encodeURIComponent(name)}`));
  }

  /** Um arquivo do pacote, sob demanda — o inventário vem no detalhe. */
  static async catalogResource(
    target: SkillScopeTarget,
    name: string,
    path: string,
  ): Promise<SkillResourceContent> {
    return getApi<SkillResourceContent>(
      this.rota(target, `/storefront/${encodeURIComponent(name)}/resource`, { path }),
    );
  }

  /** O que este escopo tem instalado. */
  static async listInstalled(target: SkillScopeTarget): Promise<InstalledSkill[]> {
    const data = await getApi<{ skills: InstalledSkill[] }>(this.rota(target));

    return data.skills ?? [];
  }

  /** A skill instalada aqui, com as integrações escolhidas e as tools (AC22). */
  static async installedDetail(
    target: SkillScopeTarget,
    name: string,
  ): Promise<InstalledSkillDetail> {
    return getApi<InstalledSkillDetail>(this.rota(target, `/${encodeURIComponent(name)}`));
  }

  /**
   * As versões publicadas da skill, da mais nova para a mais antiga.
   *
   * Vem do catálogo, e não da instalação: a lista é do **procedimento**. É útil
   * justamente para comparar as duas — a instalação fica presa a uma versão, e é
   * aqui que se vê o que veio depois dela.
   */
  static async versions(target: SkillScopeTarget, name: string): Promise<SkillVersion[]> {
    const data = await getApi<{ versions: SkillVersion[] }>(
      this.rota(target, `/${encodeURIComponent(name)}/versions`),
    );

    return data.versions ?? [];
  }

  /**
   * O que muda ao levar a instalação para a versão corrente.
   *
   * `null` quando não há atualização — a api devolve 204, e distinguir isso de
   * erro é justamente o que evita a tela mostrar alarme onde não há novidade.
   */
  static async updatePreview(
    target: SkillScopeTarget,
    name: string,
    version?: number,
  ): Promise<SkillUpdatePreview | null> {
    // Sem versão, a prévia é a da corrente do catálogo; com versão, é a da
    // troca pedida — inclusive para trás.
    return getApi<SkillUpdatePreview | null>(
      this.rota(
        target,
        `/${encodeURIComponent(name)}/update-preview`,
        version === undefined ? {} : { version: String(version) },
      ),
    );
  }

  /** Quem instalou e quem desinstalou esta skill neste escopo. */
  static async history(target: SkillScopeTarget, name: string): Promise<SkillHistoryEntry[]> {
    const data = await getApi<{ history: SkillHistoryEntry[] }>(
      this.rota(target, `/${encodeURIComponent(name)}/history`),
    );

    return data.history ?? [];
  }

  /**
   * Instala a skill neste escopo, fixando a versão corrente do catálogo.
   *
   * A versão não é escolhida aqui de propósito: trocar de versão é uma decisão à
   * parte, feita pelo `update`.
   *
   * `integrations` traz uma entrada por integração escolhida. Um requisito por
   * categoria aceita **mais de uma** (AC15), e por isso a lista pode repetir o
   * mesmo `requirement` com integrações diferentes.
   */
  static async install(
    target: SkillScopeTarget,
    name: string,
    integrations: Array<{ requirement: string; integration_base_id: string }> = [],
    parameters: Record<string, string> = {},
  ): Promise<InstalledSkill> {
    return postApi<InstalledSkill>(this.rota(target), { name, integrations, parameters });
  }

  /**
   * Troca os parâmetros de uma skill já instalada.
   *
   * Separado do `update`, que troca a VERSÃO: são decisões diferentes e uma não
   * deve arrastar a outra — trocar o framework de priorização não pode mudar o
   * texto do procedimento por baixo.
   */
  static async saveParameters(
    target: SkillScopeTarget,
    name: string,
    parameters: Record<string, string>,
  ): Promise<void> {
    await putApi<void>(this.rota(target, `/${encodeURIComponent(name)}/parameters`), {
      parameters,
    });
  }

  /**
   * Leva a skill instalada para a versão corrente do catálogo.
   *
   * Ação explícita, nunca automática: a skill é um procedimento que o time já
   * validou, e trocar o texto por baixo mudaria o resultado sem decisão.
   */
  static async update(
    target: SkillScopeTarget,
    name: string,
    version?: number,
  ): Promise<InstalledSkill> {
    return putApi<InstalledSkill>(
      this.rota(target, `/${encodeURIComponent(name)}`),
      version === undefined ? {} : { version },
    );
  }

  /** Deixa de servir a skill aqui. O registro é preservado, e ela pode voltar. */
  static async uninstall(target: SkillScopeTarget, name: string): Promise<void> {
    await delApi<void>(this.rota(target, `/${encodeURIComponent(name)}`));
  }
}
